import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { IMAGES } from "@/lib/data";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/network", label: "Network" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "bg-cream/90 backdrop-blur-xl border-b border-charcoal/10 shadow-[0_8px_30px_rgba(26,26,26,0.05)]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex items-center justify-between h-20 lg:h-24">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center shrink-0">
          <img src={IMAGES.logo} alt="Twins Media" className="h-11 lg:h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}-link`}
              className={({ isActive }) =>
                `relative text-sm uppercase tracking-[0.18em] transition-colors duration-300 whitespace-nowrap ${
                  scrolled
                    ? isActive ? "text-maroon" : "text-charcoal/70 hover:text-maroon"
                    : isActive ? "text-gold" : "text-cream/90 hover:text-gold"
                } after:absolute after:-bottom-2 after:left-0 after:h-px after:bg-gold after:transition-all after:duration-300 ${
                  isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 xl:gap-4">
          <a href="tel:+919980549123" className={`hidden xl:flex items-center gap-2 text-sm whitespace-nowrap transition-colors ${scrolled ? "text-charcoal/70 hover:text-maroon" : "text-cream/90 hover:text-gold"}`}>
            <Phone size={15} className="text-gold" /> +91 99805 49123
          </a>
          <Link
            to="/contact"
            data-testid="header-quote-btn"
            className="bg-maroon text-cream px-6 py-3 text-xs uppercase tracking-[0.18em] hover:bg-maroon-hover transition-colors duration-300 whitespace-nowrap"
          >
            Get a Quote
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden p-2 transition-colors ${scrolled ? "text-maroon" : "text-cream"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-cream border-t border-charcoal/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}-link`}
                  className="font-display text-3xl text-maroon"
                >
                  {n.label}
                </NavLink>
              ))}
              <a href="tel:+919980549123" className="flex items-center gap-2 text-maroon text-lg">
                <Phone size={18} className="text-gold" /> +91 99805 49123
              </a>
              <Link to="/contact" className="mt-2 bg-maroon text-cream px-6 py-4 text-xs uppercase tracking-[0.2em] text-center">
                Get a Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
